/**
 * `/recruiting/outreach` — the Outreach screen, WP-W11 (Phase 5 SPA, consuming the
 * Task 18 outreach API). `web/shapes/outreach.md` is the agreed shape, written
 * before this file.
 *
 * The recruiting surface whose subject is not a candidate's progress but the
 * **compliance posture of contacting them at all** — channel policy, consent and
 * suppression are compliance controls, not conveniences, and this screen's whole
 * job is to make that posture impossible to miss (§1 of the shape).
 *
 * ── Two personas, two gates, one screen ───────────────────────────────────────
 *
 * The menu item's own `requires` is `recruiting.manage_candidates` — the floor every
 * section but one needs, and what the route guard checks. **Channel policy** is
 * different: `workforce.manage_policies`, the same admin code
 * `POST /geo/office-locations` uses, because it decides which channels the company
 * may use at all, not anything about one candidate. `GeoScreen`'s own split between
 * `mayViewTeam`/`mayManagePolicies` sections is the template this screen follows —
 * the section is rendered only for a session holding the code, never the
 * authorisation, which the server still runs on every call.
 *
 * ── No "send" button anywhere ─────────────────────────────────────────────────
 *
 * A message opens `pending_approval` on create and the only client action is
 * Approve. Sending is `dispatch_outreach`, a worker job evaluating five gates on its
 * own schedule — 02 §12's rule that evaluation runs in the worker and not the
 * request path applies here exactly as it does to an alert. This screen has no
 * control that claims otherwise.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  ChannelPolicy,
  ChannelPolicyListResponse,
  ChannelPolicyUpsertRequest,
  OutreachChannel,
  OutreachConsent,
  OutreachConsentCreateRequest,
  OutreachMessage,
  OutreachMessageCreateRequest,
  OutreachMessageListResponse,
  OutreachSuppression,
  OutreachSuppressionCreateRequest,
} from '../../api/outreach';
import {
  OUTREACH_CHANNEL_POLICY_PATH,
  OUTREACH_CONSENT_PATH,
  OUTREACH_MESSAGES_PATH,
  OUTREACH_SUPPRESS_PATH,
  outreachMessageApprovePath,
  outreachMessagesPath,
} from '../../api/outreach';
import type { RecruitingCandidateListResponse } from '../../api/recruiting';
import { recruitingCandidatesPath } from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_MESSAGE_STATUS_BADGE,
  OUTREACH_MESSAGE_STATUS_LABEL,
} from '../../recruiting/outreach';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { ChannelPolicyPanel } from './ChannelPolicyPanel';
import { CreateOutreachMessageForm } from './CreateOutreachMessageForm';
import { OutreachCompliancePanel } from './OutreachCompliancePanel';
import { OutreachMessageControls } from './OutreachMessageControls';
import type { PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const MESSAGES_LIMIT = 100;

export function OutreachScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const permissions = user?.permissions ?? [];
  const mayManagePolicies = permissions.includes('workforce.manage_policies');
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';

  /* ── Candidates enrichment — a picker's names, or an honest fallback ──────── */
  const candidates = useApiQuery<RecruitingCandidateListResponse>(
    recruitingCandidatesPath({ limit: PAGE_LIMIT }),
  );
  const candidateOptions: readonly PickerOption[] | null = useMemo(() => {
    if (candidates.status !== 'ok' || candidates.data === null) return null;
    return candidates.data.data.map((cand) => ({ id: cand.id, label: cand.full_name }));
  }, [candidates.status, candidates.data]);

  /* ── Channel policy (workforce.manage_policies only) ──────────────────────── */
  const policies = useApiQuery<ChannelPolicyListResponse>(
    mayManagePolicies ? OUTREACH_CHANNEL_POLICY_PATH : null,
  );
  const savePolicy = useApiMutation<ChannelPolicyUpsertRequest, ChannelPolicy>(
    OUTREACH_CHANNEL_POLICY_PATH,
    'POST',
  );
  const [savingChannel, setSavingChannel] = useState<OutreachChannel | null>(null);
  const submitPolicy = (body: ChannelPolicyUpsertRequest): void => {
    savePolicy.reset();
    setSavingChannel(body.channel);
    savePolicy
      .submit(body)
      .then(() => {
        policies.reload();
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal is already on screen. */
      });
  };

  /* ── Messages: create, list, approve ──────────────────────────────────────── */
  const [messagesToken, setMessagesToken] = useState(0);
  const messages = useApiQuery<OutreachMessageListResponse>(
    outreachMessagesPath({ limit: MESSAGES_LIMIT }),
    messagesToken,
  );
  const createMessage = useApiMutation<OutreachMessageCreateRequest, OutreachMessage>(
    OUTREACH_MESSAGES_PATH,
    'POST',
  );
  const submitCreateMessage = (body: OutreachMessageCreateRequest): void => {
    createMessage
      .submit(body)
      .then(() => {
        setMessagesToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const approveMessage = useApiMutation<undefined, OutreachMessage>(
    outreachMessageApprovePath('unset'),
    'POST',
  );
  const [actionMessageId, setActionMessageId] = useState<string | null>(null);
  const submitApprove = (messageId: string): void => {
    approveMessage.reset();
    setActionMessageId(messageId);
    approveMessage
      .submit(undefined, outreachMessageApprovePath(messageId))
      .then(() => {
        setMessagesToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const messageRows: readonly OutreachMessage[] = messages.data?.data ?? [];

  /* ── Consent + suppression: record only ───────────────────────────────────── */
  const recordConsent = useApiMutation<OutreachConsentCreateRequest, OutreachConsent>(
    OUTREACH_CONSENT_PATH,
    'POST',
  );
  const submitConsent = (body: OutreachConsentCreateRequest): void => {
    recordConsent.submit(body).catch(() => {
      /* resolves rather than rejects */
    });
  };

  const addSuppression = useApiMutation<OutreachSuppressionCreateRequest, OutreachSuppression>(
    OUTREACH_SUPPRESS_PATH,
    'POST',
  );
  const submitSuppress = (body: OutreachSuppressionCreateRequest): void => {
    addSuppression.submit(body).catch(() => {
      /* resolves rather than rejects */
    });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.outreach')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('outreach.intro')}</p>
      </div>

      {mayManagePolicies ? (
        <ChannelPolicyPanel
          status={policies.status}
          policies={policies.data?.data ?? null}
          error={policies.error}
          onReload={policies.reload}
          savingChannel={savingChannel}
          savePending={savePolicy.status === 'pending'}
          saveError={savePolicy.error}
          saved={savePolicy.status === 'ok' ? savePolicy.data : null}
          onSave={submitPolicy}
          locale={locale}
          timezone={timezone}
        />
      ) : null}

      <section className="wp-section" aria-labelledby="w11-list-heading" data-testid="w11-messages">
        <h2 className="wp-section__title" id="w11-list-heading">
          {t('outreach.messages.title')}
        </h2>

        {messages.status === 'loading' ? (
          <Skeleton
            rows={3}
            variant="block"
            label={t('outreach.messages.subjectWord')}
            testId="w11-list-skeleton"
          />
        ) : null}
        {messages.status === 'error' && messages.error !== null ? (
          <ApiErrorNote
            error={messages.error}
            onRetry={messages.reload}
            subject={t('outreach.messages.subjectWord')}
            testId="w11-list-error"
          />
        ) : null}

        {messages.data === null ? null : messageRows.length === 0 ? (
          <EmptyState
            title={t('outreach.messages.empty.title')}
            description={t('outreach.messages.empty.body')}
            testId="w11-list-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w11-list">
            {messageRows.map((message) => (
              <li className="wp-row" key={message.id} data-testid={`w11-message-${message.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={OUTREACH_MESSAGE_STATUS_BADGE[message.status]}
                      icon={SEVERITY_ICON[OUTREACH_MESSAGE_STATUS_BADGE[message.status]]}
                      label={t(OUTREACH_MESSAGE_STATUS_LABEL[message.status])}
                      testId={`w11-message-status-${message.id}`}
                    />{' '}
                    <span data-testid={`w11-message-channel-${message.id}`}>
                      {t(OUTREACH_CHANNEL_LABEL[message.channel])}
                    </span>
                  </p>
                  <p
                    className="wp-small wp-muted"
                    data-testid={`w11-message-consent-${message.id}`}
                  >
                    {message.consent_id === null
                      ? t('outreach.messages.row.noConsent')
                      : t('outreach.messages.row.hasConsent')}
                  </p>
                  {message.error_message === null ? null : (
                    <p
                      className="wp-small wp-muted"
                      data-testid={`w11-message-error-${message.id}`}
                    >
                      {message.error_message}
                    </p>
                  )}
                </div>
                <div className="wp-row__figure">
                  <OutreachMessageControls
                    messageId={message.id}
                    status={message.status}
                    pending={actionMessageId === message.id && approveMessage.status === 'pending'}
                    error={actionMessageId === message.id ? approveMessage.error : null}
                    onApprove={() => {
                      submitApprove(message.id);
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <OutreachCompliancePanel
        candidateOptions={candidateOptions}
        consentPending={recordConsent.status === 'pending'}
        consentError={recordConsent.error}
        consentRecorded={recordConsent.status === 'ok' ? recordConsent.data : null}
        onRecordConsent={submitConsent}
        suppressPending={addSuppression.status === 'pending'}
        suppressError={addSuppression.error}
        suppressed={addSuppression.status === 'ok' ? addSuppression.data : null}
        onSuppress={submitSuppress}
      />

      {/* The create form is the last section, not the first: a recruiter opens this
          screen to read the list, and opening a new one is the occasional action.
          Leading with the form put the subject of the page below the fold. */}
      <CreateOutreachMessageForm
        candidateOptions={candidateOptions}
        pending={createMessage.status === 'pending'}
        error={createMessage.error}
        created={createMessage.status === 'ok' ? createMessage.data : null}
        onSubmit={submitCreateMessage}
      />
    </div>
  );
}
